// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.PermissionRoleSetup.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React from 'react';
import PropTypes from 'prop-types';
import connect from 'share/enhancers/Izenda.Connect';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import Input from 'share/components/controls/Izenda.Input';
import actions from '../Izenda.PermissionActions';
import { SECTION } from 'logic/data/Izenda.PermissionConstants';
import CheckboxAllPermission from './Izenda.CheckboxAllPermission';
import { isNil, get } from 'lodash/fp';

const PermissionRoleSetup = (props: any) => {
  const {
    permissionStore: { tenantDetail, isShowRoleSetup },
    permission,
    permission: { roleSetup, isTenantSetup },
    localization,
    readOnly,
    isTemplatePermissions
  } = props;
  const tenantRoleSetup = get('roleSetup', tenantDetail);

  return (
    (isNil(tenantDetail) || props.checkShowGroupCommon(tenantDetail.roleSetup)) && (
      <div className={SECTION.RoleSetup}>
        <GridLayout className='izenda-GridLayout-Custom'>
          <GridLayout.Row className='u_textBold u_paddingTop u_paddingBottom izenda-PermissionCategory-Header'>
            <GridLayout.Col cols={2}>
              <a className={props.getIconClass('isShowRoleSetup')} onClick={props.toggle('isShowRoleSetup')} />
              {localization.getText('COMMON_LABEL_ROLE_SETUP')}
            </GridLayout.Col>
            <GridLayout.Col cols={9}>
              <CheckboxAllPermission
                permission={permission}
                readOnly={readOnly}
                disabled={isTemplatePermissions}
                section={roleSetup}
                tenantPermission={tenantRoleSetup}
                tenantDetail={tenantDetail}
              />
            </GridLayout.Col>
            {isTenantSetup && (
              <GridLayout.Col cols={1} className='u_textCenter'>
                <Input
                  type='checkbox'
                  readOnly={readOnly}
                  disabled={isTemplatePermissions}
                  indeterminate={props.getIndeterminateCommon(roleSetup)}
                  checked={props.getCheckedCommon(roleSetup.tenantAccess)}
                  onChange={props.permissionUpdateTenantAccessParent(roleSetup)}
                />
              </GridLayout.Col>
            )}
          </GridLayout.Row>
        </GridLayout>
        {isShowRoleSetup && (
          <GridLayout className='izenda-GridLayout-Custom'>
            {(isNil(tenantDetail) || props.checkShowGroupCommon(tenantDetail.roleSetup.actions)) && (
              <GridLayout.Row className='u_paddingTop u_paddingBottom'>
                <GridLayout.Col cols={2} className='izenda-PermissionCategory-SubCategory'>
                  {localization.getText('COMMON_LABEL_ACTIONS')}
                </GridLayout.Col>
                <GridLayout.Col cols={9}>
                  {(isNil(tenantDetail) || tenantDetail.roleSetup.actions.create) && (
                    <div className='u_inlineBlock izenda-PermissionCategory-group_left'>
                      <label>
                        <Input
                          type='checkbox'
                          readOnly={readOnly}
                          disabled={isTemplatePermissions}
                          checked={props.getCheckedCommon(roleSetup.actions.create)}
                          onChange={props.permissionUpdateData(roleSetup.actions, 'create')}
                        />
                        <span>{localization.getText('COMMON_LABEL_CREATE')}</span>
                      </label>
                    </div>
                  )}
                  {(isNil(tenantDetail) || tenantDetail.roleSetup.actions.edit) && (
                    <div className='u_inlineBlock izenda-PermissionCategory-group_left'>
                      <label>
                        <Input
                          type='checkbox'
                          readOnly={readOnly}
                          disabled={isTemplatePermissions}
                          checked={props.getCheckedCommon(roleSetup.actions.edit)}
                          onChange={props.permissionUpdateData(roleSetup.actions, 'edit')}
                        />
                        <span>{localization.getText('COMMON_LABEL_EDIT')}</span>
                      </label>
                    </div>
                  )}
                  {(isNil(tenantDetail) || tenantDetail.roleSetup.actions.del) && (
                    <div className='u_inlineBlock izenda-PermissionCategory-group_left'>
                      <label>
                        <Input
                          type='checkbox'
                          readOnly={readOnly}
                          disabled={isTemplatePermissions}
                          checked={props.getCheckedCommon(roleSetup.actions.del)}
                          onChange={props.permissionUpdateData(roleSetup.actions, 'del')}
                        />
                        <span>{localization.getText('COMMON_LABEL_DELETE')}</span>
                      </label>
                    </div>
                  )}
                </GridLayout.Col>
                {isTenantSetup && (
                  <GridLayout.Col cols={1} className='u_textCenter'>
                    <Input
                      type='checkbox'
                      readOnly={readOnly}
                      disabled={isTemplatePermissions}
                      checked={props.getCheckedCommon(roleSetup.actions.tenantAccess)}
                      onChange={props.permissionUpdateTenantAccess(roleSetup, roleSetup.actions)}
                    />
                  </GridLayout.Col>
                )}
              </GridLayout.Row>
            )}
            {(isNil(tenantDetail) || props.checkShowGroupCommon(tenantDetail.roleSetup.dataModelAccess)) && (
              <GridLayout.Row className='u_paddingTop u_paddingBottom'>
                <GridLayout.Col cols={2} className='izenda-PermissionCategory-SubCategory'>
                  {localization.getText('PERMISSION_LABEL_DATA_MODEL_ACCESS')}
                </GridLayout.Col>
                <GridLayout.Col cols={9}>
                  <div className='u_inlineBlock izenda-PermissionCategory-group_left'>
                    <Input
                      type='checkbox'
                      readOnly={readOnly}
                      disabled={isTemplatePermissions}
                      checked={props.getCheckedCommon(roleSetup.dataModelAccess.value)}
                      onChange={props.permissionUpdateData(roleSetup.dataModelAccess, 'value')}
                    />
                  </div>
                </GridLayout.Col>
                {isTenantSetup && (
                  <GridLayout.Col cols={1} className='u_textCenter'>
                    <Input
                      type='checkbox'
                      readOnly={readOnly}
                      disabled={isTemplatePermissions}
                      checked={props.getCheckedCommon(roleSetup.dataModelAccess.tenantAccess)}
                      onChange={props.permissionUpdateTenantAccess(roleSetup, roleSetup.dataModelAccess)}
                    />
                  </GridLayout.Col>
                )}
              </GridLayout.Row>
            )}
            {(isNil(tenantDetail) || props.checkShowGroupCommon(tenantDetail.roleSetup.permissions)) && (
              <GridLayout.Row className='u_paddingTop u_paddingBottom'>
                <GridLayout.Col cols={2} className='izenda-PermissionCategory-SubCategory'>
                  {localization.getText('PERMISSION_LABEL_PERMISSIONS')}
                </GridLayout.Col>
                <GridLayout.Col cols={9}>
                  <div className='u_inlineBlock izenda-PermissionCategory-group_left'>
                    <Input
                      type='checkbox'
                      readOnly={readOnly}
                      disabled={isTemplatePermissions}
                      checked={props.getCheckedCommon(roleSetup.permissions.value)}
                      onChange={props.permissionUpdateData(roleSetup.permissions, 'value')}
                    />
                  </div>
                </GridLayout.Col>
                {isTenantSetup && (
                  <GridLayout.Col cols={1} className='u_textCenter'>
                    <Input
                      type='checkbox'
                      readOnly={readOnly}
                      disabled={isTemplatePermissions}
                      checked={props.getCheckedCommon(roleSetup.permissions.tenantAccess)}
                      onChange={props.permissionUpdateTenantAccess(roleSetup, roleSetup.permissions)}
                    />
                  </GridLayout.Col>
                )}
              </GridLayout.Row>
            )}
            {(isNil(tenantDetail) ||
              props.checkShowGroupCommon(tenantDetail.roleSetup.grantRoleWithFullReportAndDashboardAccess)) && (
              <GridLayout.Row className='u_paddingTop u_paddingBottom'>
                <GridLayout.Col cols={2} className='izenda-PermissionCategory-SubCategory'>
                  {localization.getText('PERMISSION_LABEL_GRANT_ROLE_WITH_FULL_REPORT_AND_DASHBOARD_ACCESS')}
                </GridLayout.Col>
                <GridLayout.Col cols={9}>
                  <div className='u_inlineBlock izenda-PermissionCategory-group_left'>
                    <Input
                      type='checkbox'
                      checked={props.getCheckedCommon(roleSetup.grantRoleWithFullReportAndDashboardAccess.value)}
                      onChange={props.permissionUpdateData(
                        roleSetup.grantRoleWithFullReportAndDashboardAccess,
                        'value'
                      )}
                      readOnly={readOnly}
                      disabled={isTemplatePermissions}
                    />
                  </div>
                </GridLayout.Col>
                {isTenantSetup && (
                  <GridLayout.Col cols={1} className='u_textCenter'>
                    <Input
                      type='checkbox'
                      readOnly={readOnly}
                      disabled={isTemplatePermissions}
                      checked={props.getCheckedCommon(roleSetup.grantRoleWithFullReportAndDashboardAccess.tenantAccess)}
                      onChange={props.permissionUpdateTenantAccess(
                        roleSetup,
                        roleSetup.grantRoleWithFullReportAndDashboardAccess
                      )}
                    />
                  </GridLayout.Col>
                )}
              </GridLayout.Row>
            )}
          </GridLayout>
        )}
      </div>
    )
  );
};

PermissionRoleSetup.propTypes = {
  permission: PropTypes.object.isRequired
};

export default connect(actions)(PermissionRoleSetup);



// WEBPACK FOOTER //
// ./ui/modules/Settings/PermissionTab/PermissionBody/Izenda.PermissionRoleSetup.js